// Reusable 👍/👎 + optional-comment control, shared by every Claude Agent SDK
// surface — the admin Agent Console, the Quality Co-Pilot, and the Tech Support
// Co-Pilot (mirrors the one shared ModelSelect picker). It posts to the door's
// POST /conversations/{id}/feedback via the shared submitFeedback() helper, so a
// reaction lands on the real conversation/message audit row.
//
// On a 👎 of a turn that ran on less than the strongest model, it shows a gentle
// "try a stronger model" nudge (the strongest id/label come from GET /models, so
// nothing hardcodes the flagship).

import { useState } from 'react';
import {
  submitFeedback,
  type FeedbackContext,
  type FeedbackRating,
} from '../../services/agentConsole';
import styles from './FeedbackControls.module.css';

export default function FeedbackControls({ context }: { context: FeedbackContext }) {
  const [rating, setRating] = useState<FeedbackRating | null>(null);
  const [comment, setComment] = useState('');
  const [showComment, setShowComment] = useState(false);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const send = async (nextRating: FeedbackRating | null, nextComment: string) => {
    const trimmed = nextComment.trim();
    if (nextRating === null && !trimmed) return; // nothing to submit
    setBusy(true);
    setError(null);
    try {
      await submitFeedback(context.basePath, context.conversationId, {
        rating: nextRating,
        comment: trimmed || null,
        message_id: context.messageId,
      });
      setSaved(true);
    } catch {
      setError('Could not save feedback — try again.');
    } finally {
      setBusy(false);
    }
  };

  // Clicking a thumb records the rating immediately (one-click reaction) and
  // reveals the optional comment box so detail can follow without blocking.
  const choose = (next: FeedbackRating) => {
    setRating(next);
    setShowComment(true);
    setSaved(false);
    void send(next, comment);
  };

  // Suggest the flagship only on a 👎 of a turn that ran on a weaker model.
  const showStrongerNudge =
    rating === 'down' &&
    !!context.strongestModelId &&
    !!context.modelUsed &&
    context.modelUsed !== context.strongestModelId;

  return (
    <div className={styles.wrap}>
      <div className={styles.row}>
        <span className={styles.label}>Was this helpful?</span>
        <button
          type="button"
          className={`${styles.thumb} ${rating === 'up' ? styles.thumbUpActive : ''}`}
          onClick={() => choose('up')}
          disabled={busy}
          aria-pressed={rating === 'up'}
          aria-label="Thumbs up"
          title="Helpful"
        >
          👍
        </button>
        <button
          type="button"
          className={`${styles.thumb} ${rating === 'down' ? styles.thumbDownActive : ''}`}
          onClick={() => choose('down')}
          disabled={busy}
          aria-pressed={rating === 'down'}
          aria-label="Thumbs down"
          title="Not helpful"
        >
          👎
        </button>
        {saved && !error && <span className={styles.saved}>Thanks for the feedback</span>}
        {error && <span className={styles.error} role="alert">{error}</span>}
      </div>

      {showStrongerNudge && (
        <div className={styles.nudge} role="note">
          Didn't get the right result? Try a stronger model
          {context.strongestModelLabel ? `: ${context.strongestModelLabel}` : ''}.
        </div>
      )}

      {showComment && (
        <div className={styles.commentRow}>
          <textarea
            className={styles.commentBox}
            value={comment}
            placeholder="Add a comment (optional)…"
            rows={2}
            onChange={(e) => setComment(e.target.value)}
            disabled={busy}
          />
          <button
            type="button"
            className={styles.sendBtn}
            onClick={() => void send(rating, comment)}
            disabled={busy || !comment.trim()}
          >
            Send
          </button>
        </div>
      )}
    </div>
  );
}
